import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default class ScrollNote {
    constructor() {
        this.initDisplay = false;
        this.lineDisplay = false;
        this.itemTimelines = []; // 各アイテムのタイムラインを保持
        this.activeIndex = -1; // 現在アクティブなアイテムのインデックス

        // アニメーションタイミング設定
        this.TIMING = {
            INDEX_PLUS: 0, // .index と .plus の開始タイミング
            NAME: 0.4, // .name の開始タイミング
            LINE: 0.8, // .line の開始タイミング
            TEXT_FIRST: 1.1, // 最初の .text の開始タイミング
            TEXT_SECOND: 1.5, // 2番目の .text の開始タイミング
        };

        // 初期化を最後に呼ぶ
        this.init();
    }

    init() {
        this.mm = gsap.matchMedia();

        this.mm.add("(min-width:780px)", () => {
            this.noteScrollSnap();

            // クリーンアップ
            return () => {
                this.itemTimelines.forEach((tl) => tl?.kill());
                this.itemTimelines = [];
                ScrollTrigger.getAll().forEach((st) => st.kill());
                this.resetNote();
            };
        });

        this.mm.add("(max-width:779px)", () => {
            this.resetNote();
        });

        this.scrollDirectionJudge();
    }

    addClassPagerIn() {
        const pager = document.querySelector(".js-artistnote-pager");

        if (!this.initDisplay && pager) {
            pager.classList.add("-pagerin");
            this.initDisplay = true;
        }
    }

    removeClassPagerIn() {
        const pager = document.querySelector(".js-artistnote-pager");

        if (pager) {
            pager.classList.remove("-pagerin");
            this.initDisplay = false;
        }
    }

    addClassLineIn() {
        const cmt = document.querySelector(".js-artistnote-cmt");

        if (cmt) {
            cmt.classList.add("-linein");
            this.lineDisplay = true;
        }
    }

    removeClassLineIn() {
        const cmt = document.querySelector(".js-artistnote-cmt");

        if (cmt) {
            cmt.classList.remove("-linein");
            this.lineDisplay = false;
        }
    }

    noteScrollSnap() {
        const wrapper = document.querySelector(".js-artistnote");
        const cont = document.querySelector(".js-artistnote-cont");
        const steps = gsap.utils.toArray(".js-artistnote-item");
        if (!wrapper || !cont || steps.length === 0) return;

        const slice = 1 / steps.length;

        // 各アイテムごとのアニメーションタイムラインを作成
        steps.forEach((step, idx) => {
            const tl = gsap.timeline({ paused: true });

            const plus = step.querySelector(".plus");
            const indexChars = step.querySelectorAll(".index .js-char");
            const nameChars = step.querySelectorAll(".name .js-char");
            const line = step.querySelector(".js-line");
            const texts = step.querySelectorAll(".note .text.js-char");

            // indexとplusのアニメーション
            if (indexChars.length > 0) {
                tl.fromTo(
                    indexChars,
                    { y: "110%", opacity: 1 },
                    {
                        y: "0%",
                        opacity: 1,
                        duration: 0.8,
                        ease: "expo.out",
                        onStart: () => this.addClassPagerIn(),
                    },
                    this.TIMING.INDEX_PLUS
                );
            }

            if (plus) {
                tl.fromTo(
                    plus,
                    { y: "110%", opacity: 1, visibility: "hidden" },
                    {
                        y: "0%",
                        opacity: 1,
                        visibility: "visible",
                        duration: 0.8,
                        ease: "expo.out",
                        onStart: () => this.addClassPagerIn(),
                    },
                    this.TIMING.INDEX_PLUS
                );
            }

            // nameのアニメーション
            if (nameChars.length > 0) {
                tl.fromTo(
                    nameChars,
                    { y: "110%", opacity: 1 },
                    {
                        y: "0%",
                        opacity: 1,
                        duration: 0.9,
                        ease: "expo.out",
                    },
                    this.TIMING.NAME
                );
            }

            // noteのlineとtextのアニメーション
            if (line) {
                tl.fromTo(
                    line,
                    { opacity: 0, scaleX: 20, transformOrigin: "left center" },
                    {
                        opacity: 1,
                        scaleX: 1,
                        duration: 1.5,
                        ease: "expo.out",
                        onStart: () => this.addClassLineIn(),
                    },
                    this.TIMING.LINE
                );
            }

            texts.forEach((text, textIdx) => {
                const delay = textIdx === 0 ? this.TIMING.TEXT_FIRST : this.TIMING.TEXT_SECOND;
                tl.fromTo(text, { y: "5%", opacity: 0 }, { y: "0%", opacity: 1, duration: 0.5, ease: "ease.out" }, delay);
            });

            this.itemTimelines[idx] = tl;
        });

        // ScrollTrigger でアクティブなアイテムを判定（アニメーション用）
        ScrollTrigger.create({
            trigger: wrapper,
            start: "top top",
            end: "bottom top",
            onUpdate: (self) => {
                const progress = self.progress;
                this.checkProgressAndPlayTimeline(progress, steps, wrapper, slice);
            },
        });

        // ScrollTrigger でリセット判定（js-artistnote-cont用）
        ScrollTrigger.create({
            trigger: cont,
            start: "top bottom",
            end: "bottom bottom",
            onLeaveBack: () => {
                // 上にスクロールしてcontの上端が画面下に消えた時
                this.removeClassPagerIn();
                this.removeClassLineIn();
                this.resetAllItems(steps);
                this.activeIndex = -1;
            },
        });

        const pagerButtons = wrapper.querySelectorAll(".js-artistnote-pager-btn");
        if (pagerButtons.length && !wrapper.dataset.noteitemPagerBound) {
            wrapper.dataset.noteitemPagerBound = "1";
            pagerButtons.forEach((button, idx) => {
                button.addEventListener("click", () => {
                    wrapper.dataset.noteitem = String(idx + 1);
                    const wrapperTop = wrapper.getBoundingClientRect().top + window.scrollY;
                    const boundaryProgress = (idx + 0.5) / steps.length;
                    const scrollY = wrapperTop + wrapper.offsetHeight * boundaryProgress;
                    window.scrollTo({ top: scrollY, behavior: "smooth" });
                });
            });
        }
    }

    checkProgressAndPlayTimeline(progress, steps, wrapper, slice) {
        let newActiveIndex = -1;

        steps.forEach((step, i) => {
            const start = i * slice;
            const end = start + slice;
            let s = 0;

            if (progress >= start && progress <= end) {
                s = (progress - start) / slice;
            } else if (progress > end) {
                s = 1;
            }

            // アクティブ判定
            if (s > 0 && s < 1) {
                step.classList.add("-active");
                newActiveIndex = i;
            } else {
                step.classList.remove("-active");
            }

            step.style.setProperty("--progress", s);
        });

        // アクティブなアイテムが変わった場合
        if (newActiveIndex !== -1 && newActiveIndex !== this.activeIndex) {
            // 前のアイテムのタイムラインを停止してリセット
            if (this.activeIndex !== -1 && this.activeIndex !== newActiveIndex) {
                if (this.itemTimelines[this.activeIndex]) {
                    this.itemTimelines[this.activeIndex].pause();
                }
                this.resetItem(steps[this.activeIndex]);
            }

            // 新しいアクティブインデックスを設定
            this.activeIndex = newActiveIndex;
            wrapper.dataset.noteitem = String(newActiveIndex + 1);

            // 該当するタイムラインを最初から再生
            if (this.itemTimelines[newActiveIndex]) {
                this.itemTimelines[newActiveIndex].restart();
            }
        }
    }

    // 個別のアイテムをリセットする新しいメソッド
    resetItem(step) {
        if (!step) return;

        // 各要素を初期状態に戻す
        gsap.set(step.querySelectorAll(".index .js-char"), { y: "110%", opacity: 0 });
        gsap.set(step.querySelectorAll(".plus"), { y: "110%", opacity: 0, visibility: "hidden" });
        gsap.set(step.querySelectorAll(".name .js-char"), { y: "110%", opacity: 0 });
        gsap.set(step.querySelectorAll(".js-line"), { opacity: 0, scaleX: 20 });
        gsap.set(step.querySelectorAll(".note .text.js-char"), { y: "5%", opacity: 0 });
    }

    // 全アイテムをリセットするメソッド
    resetAllItems(steps) {
        // すべてのタイムラインを停止
        this.itemTimelines.forEach((tl) => {
            if (tl) tl.pause();
        });

        // 全アイテムを初期化
        steps.forEach((step) => {
            this.resetItem(step);
        });
    }

    resetNote() {
        const wrapper = document.querySelector(".js-artistnote");
        if (!wrapper) return;

        delete wrapper.dataset.noteitem;

        gsap.utils.toArray(".js-artistnote-item", wrapper).forEach((step) => {
            step.classList.remove("-active");
            step.style.removeProperty("--progress");
            gsap.set(step.querySelectorAll(".js-char, .js-zero"), { clearProps: "all" });
            gsap.set(step.querySelectorAll(".plus-line"), { clearProps: "all" });
            gsap.set(step.querySelectorAll(".js-line"), { clearProps: "all" });
            gsap.set(step.querySelectorAll(".note .text"), { clearProps: "all" });
        });
    }

    scrollDirectionJudge() {
        this.lastScrollY = window.scrollY;

        window.addEventListener(
            "scroll",
            () => {
                const y = window.scrollY;
                this.scrollDir = y > this.lastScrollY ? "down" : "up";
                this.lastScrollY = y;
            },
            { passive: true }
        );
    }
}
